import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImageUp, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import TourLineEditor from "../tour/TourLineEditor";
import IntroMusicEditor from "./IntroMusicEditor";

const inputCls = "rounded-lg bg-navy-950/70 border border-white/10 focus:border-brand-cyan/60 outline-none px-3 py-2 text-sm text-slate-100";
const err = (e, fb) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);

const BLANK_STATS = [
  { prefix: "$", value: 0, suffix: "", label: "saved every year" },
  { prefix: "", value: 0, suffix: "", label: "hours back" },
  { prefix: "", value: 0, suffix: "", label: "tools replaced" },
];
const withStats = (i) => (i && !i.stats?.length ? { ...i, stats: BLANK_STATS } : i);

export default function IntroEditorDialog({ open, onOpenChange, slug, intro, narration, narrationStep = "IN", narrationEndpoints, onSaved, onLineSaved }) {
  const [draft, setDraft] = useState(withStats(intro));
  const [busy, setBusy] = useState(null);
  const [lineKey, setLineKey] = useState(0);
  const fileRef = useRef(null);
  useEffect(() => { if (open) setDraft(withStats(intro)); }, [open, intro]);

  const setStat = (i, k, v) => setDraft((d) => ({ ...d, stats: d.stats.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));

  const call = async (kind, fn, ok) => {
    setBusy(kind);
    try { const { data } = await fn(); onSaved(data); setDraft(withStats(data)); toast.success(ok); }
    catch (e) { toast.error(err(e, "Something went wrong")); }
    finally { setBusy(null); }
  };

  const save = () => call("save", () => apiClient.put(`/admin/demos/${slug}/intro`, {
    headline: draft.headline, tagline: draft.tagline,
    stats: draft.stats.filter((s) => s.label.trim()).map((s) => ({ ...s, value: parseInt(s.value, 10) || 0 })),
  }), "Intro saved");
  const resetStats = () => call("reset", () => apiClient.delete(`/admin/demos/${slug}/intro/stats`), "Figures synced to the demo");
  const removeLogo = () => call("logo", () => apiClient.delete(`/admin/demos/${slug}/intro/logo`), "Logo removed");
  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    call("logo", () => apiClient.post(`/admin/demos/${slug}/intro/logo`, fd), "Logo uploaded");
  };

  if (!draft) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="veroogle-intro-editor" className="bg-[#0C1322] border-white/10 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit intro</DialogTitle>
          <DialogDescription className="text-slate-400">The 5-second opener shown each time this demo opens.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-navy-950 border border-white/10 flex items-center justify-center overflow-hidden">
            {draft.logo_url
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}${draft.logo_url}`} alt="" className="w-[80%] h-[80%] object-contain" />
              : <span className="text-xs text-slate-500 font-mono">AUTO</span>}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="veroogle-intro-logo-input" />
          <button onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="veroogle-intro-logo-upload"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/10 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors disabled:opacity-40">
            {busy === "logo" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageUp className="w-3.5 h-3.5" />} Upload logo
          </button>
          {draft.logo_url && (
            <button onClick={removeLogo} disabled={!!busy} data-testid="veroogle-intro-logo-remove"
              className="p-2 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors" aria-label="Remove logo">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <label className="block space-y-1">
          <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-slate-500">Headline</span>
          <input value={draft.headline} maxLength={40} onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
            data-testid="veroogle-intro-headline-input" className={`${inputCls} w-full`} />
        </label>
        <label className="block space-y-1">
          <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-slate-500">Tagline</span>
          <input value={draft.tagline} maxLength={80} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}
            data-testid="veroogle-intro-tagline-input" className={`${inputCls} w-full`} />
        </label>

        <div className="space-y-2">
          <div className="flex items-center font-mono text-[10px] tracking-[0.16em] uppercase text-slate-500">
            Figures {draft.stats_custom ? "(custom)" : "(synced to demo)"}
            {draft.stats_custom && (
              <button onClick={resetStats} disabled={!!busy} data-testid="veroogle-intro-stats-reset"
                className="ml-auto inline-flex items-center gap-1 text-slate-400 hover:text-amber-300 transition-colors">
                <RotateCcw className="w-3 h-3" /> Sync to demo
              </button>
            )}
          </div>
          {draft.stats.map((s, i) => (
            <div key={i} className="grid grid-cols-[48px_1fr_48px_1.4fr] gap-2" data-testid={`veroogle-intro-stat-row-${i}`}>
              <input value={s.prefix} maxLength={4} placeholder="$" onChange={(e) => setStat(i, "prefix", e.target.value)} className={inputCls} data-testid={`veroogle-intro-stat-prefix-${i}`} />
              <input value={s.value} inputMode="numeric" onChange={(e) => setStat(i, "value", e.target.value.replace(/\D/g, "").slice(0, 10))} className={`${inputCls} font-mono`} data-testid={`veroogle-intro-stat-value-${i}`} />
              <input value={s.suffix} maxLength={6} placeholder="+" onChange={(e) => setStat(i, "suffix", e.target.value)} className={inputCls} data-testid={`veroogle-intro-stat-suffix-${i}`} />
              <input value={s.label} maxLength={40} onChange={(e) => setStat(i, "label", e.target.value)} className={inputCls} data-testid={`veroogle-intro-stat-label-${i}`} />
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={!!busy || !draft.headline.trim() || !draft.tagline.trim()} data-testid="veroogle-intro-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-mono text-[10px] font-semibold tracking-[0.12em] uppercase transition-colors disabled:opacity-40">
            {busy === "save" && <Loader2 className="w-3 h-3 animate-spin" />} Save intro
          </button>
        </div>

        <IntroMusicEditor key={intro.music_url || "none"} slug={slug} intro={intro} onSaved={(d) => { onSaved(d); setDraft(withStats(d)); }} />

        {narration && (
          <div className="pt-4 border-t border-white/10">
            <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-slate-500 mb-2">Spoken line</div>
            <TourLineEditor
              key={lineKey}
              step={narrationStep}
              endpoints={narrationEndpoints}
              maxLength={narrationEndpoints ? 600 : 1500}
              autoFocus={false}
              maxSeconds={11}
              text={narration.text}
              edited={narration.edited}
              onCancel={() => setLineKey((k) => k + 1)}
              onSaved={(line) => { onLineSaved(line); setLineKey((k) => k + 1); }}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
